import { ChevronLeft, Close } from '@mui/icons-material';
import { Box, Button, Checkbox, Divider, FormLabel, IconButton, Modal } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Bars } from 'react-loader-spinner';
import clsx from 'clsx';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { addNote, getNotesListById, updateNote } from '../../../actions/artist';
import NoteListView from './NoteListView';
import { useMessage } from '../../../../base/MessageProvider';
import ReactTextInput from '../../../../components/ControllerComponent/ReactTextInput';

const boxStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 400,
    width: '90%',
    bgcolor: 'white',
    paddingX: 5,
    paddingY: 3,
    borderRadius: '18px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center' 
}

const validationSchema = Yup.object().shape({
    id: Yup.string(),
    profileId: Yup.string(),
    note: Yup.string()
        .required("Please write some note before submitting")
})


const NotesModal = ({ open = false, onClose, metadata }) => {
    const dispatch = useDispatch();
    const alert = useMessage();

    const [isAddMode, setIsAddMode] = useState(false);

    const loading = useSelector((state) => state.recruiterArtistReducer?.loading);
    const notesList = useSelector((state) => state.recruiterArtistReducer?.artistNotesList) ?? [];

    const { control, handleSubmit, setValue, clearErrors, reset, formState: { errors } } = useForm({
        defaultValues: { note: '', profileId: '', id: '' },
        resolver: yupResolver(validationSchema)
    });


    useEffect(() => {
        const init = async () => {
            clearErrors(); reset();
            dispatch(getNotesListById({ profileId: metadata?.userId }));
            if (notesList?.length > 0) {
                setIsAddMode(false);
            }
        }
        if (open) init();
    }, [open]);

    const onPressUpdateButton = (item) => {
        reset();
        setIsAddMode(true);
        setValue('id', item?.id);
        setValue('note', item?.note);
    }

    const onPressAddButton = () => {
        reset();
        setIsAddMode(true);
    }

    const successCallback = async (res) => {
        alert.showSuccess(res.message);
        dispatch(getNotesListById({ profileId: metadata?.userId }));
        setIsAddMode(false);
    }

    const errorCallback = async (error) => {
        alert.showError(error?.message ?? "Something went wrong!!");
    }

    const onSubmit = async (data) => {
        const request = {
            ...data,
            profileId: metadata?.userId
        }
        if (data?.id) {
            dispatch(updateNote(request, successCallback, errorCallback));
        } else {
            dispatch(addNote(request, successCallback, errorCallback));
        }
    }

    const DISABLE_SUBMIT = loading;

    return (
        <Modal disableAutoFocus sx={{ bgcolor: '#F4F4F499'}} hideBackdrop open={open}>
            <Box className="shadow-[0_0_14px_grey]" sx={boxStyle}>
                <IconButton onClick={onClose} className='absolute top-3 right-3' size='small'> 
                    <Close className='text-xl' />
                </IconButton>
                {isAddMode ? (
                    <IconButton onClick={setIsAddMode?.bind(null, false)} className='absolute top-3 left-3' size='small'> 
                        <ChevronLeft className='text-2xl' />
                    </IconButton>
                ) : null}
                <Box className="mb-4">
                    <h2 className="font-['Poppins-Medium'] font-[500] text-[18px]">Notes</h2>
                </Box>
                <div className="flex flex-col gap-3 mt-3 w-full">
                    <Box className="w-full">
                        {isAddMode ? (
                            <Box className="my-2">
                                <ReactTextInput
                                    hookFormControl={control}
                                    varName="note"
                                    key={'note'}
                                    isMultiLine rows={4}
                                    maxLength={150}
                                    errorMessage={errors?.note?.message}
                                />
                            </Box>
                        ) : (
                            <NoteListView
                                onClickAdd={onPressAddButton}
                                onClickEdit={onPressUpdateButton}
                                metadata={metadata}
                            />
                        )}
                    </Box>
                </div>
                {isAddMode ? (
                    <Button onClick={handleSubmit(onSubmit)} disabled={DISABLE_SUBMIT} className={clsx("bg-[#2F80ED] normal-case flex gap-3 justify-center text-[#FFF] w-full rounded-full my-3 font-['Poppins-Medium'] font-[500]", { "bg-opacity-50": DISABLE_SUBMIT })}>
                        Submit
                        <Bars
                            height="20"
                            width={'20'}
                            color="#FFFFFF"
                            ariaLabel="bars-loading"
                            visible={loading}
                        />
                    </Button>
                ) : null}
            </Box>
        </Modal>
    );
}

export default React.memo(NotesModal, (prevProps, nextProps) => {
    return prevProps.open === nextProps.open
});